import {
    ORIENTATION_CHANGED,
    OrientationChangedAction,
    SOFT_KEYBOARD_TOGGLE,
    SoftKeyboardToggleAction,
} from 'mk2/actions';
import { TopicCoverUploadNormalizedResponse } from 'mk2/apps/forum/containers/Topic/Topic.actions';
import { photosUploadTrigger } from 'mk2/containers/PhotosUpload/PhotosUpload.actions';
import {
    preparePhotosUploadApi,
    TOOLBAR_INSERT_EMOJI,
    TOOLBAR_PREPARE_PHOTOS_UPLOAD_TRIGGER,
    TOOLBAR_WATCH_EMOJI_START,
    TOOLBAR_WATCH_EMOJI_STOP,
    toolbarCloseAll,
    ToolbarInsertEmojiAction,
    ToolbarPreparePhotosUploadTriggerAction,
    ToolbarWatchEmojiStartAction,
    ToolbarWatchEmojiStopAction,
} from 'mk2/containers/Toolbar/Toolbar.actions';
import { ToolbarAction } from 'mk2/containers/Toolbar/Toolbar.enums';
import { ToolbarState } from 'mk2/containers/Toolbar/Toolbar.reducers';
import { XHRAction } from 'mk2/helpers/api';
import { getPhotoPreview } from 'mk2/helpers/photos';
import { getToolbarState } from 'mk2/reducers';
import { PhotoPreviewSchema } from 'mk2/schemas';
import { normalize } from 'normalizr';
import { actionTypes, change, getFormValues } from 'redux-form';
import { all, call, cancel, fork, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';

function* keyboardToggle({ opened }: SoftKeyboardToggleAction) {
    // Close toolbar when keyboard pop-up
    if (opened) {
        yield put(toolbarCloseAll(null));
    }
}

function* orientationChanged({}: OrientationChangedAction) {
    // Close toolbar when orientation has changed
    yield put(toolbarCloseAll(null));
}

function* startEmojisWatch({ formName, fields, defaultField }: ToolbarWatchEmojiStartAction) {
    let field = defaultField;
    let activeElement: HTMLInputElement | HTMLTextAreaElement;

    const f = document.getElementsByName(field)[0];
    if (f instanceof HTMLInputElement || f instanceof HTMLTextAreaElement) {
        activeElement = f;
    }

    let selectionStart;
    let selectionEnd;

    // Start insert emoji saga - we are inserting to last focused field or default field
    const task = yield takeEvery(
        (a: ToolbarInsertEmojiAction) => a.type === TOOLBAR_INSERT_EMOJI && a.form === formName,
        function* emojiInsert({ shortcode }: ToolbarInsertEmojiAction) {
            // Current value
            const values = yield select(getFormValues(formName));
            const value = values && values[field] || '';

            selectionStart = selectionStart || (activeElement ? activeElement.selectionStart : value.length);
            selectionEnd = selectionEnd || (activeElement ? activeElement.selectionEnd : value.length);

            const head = value.substring(0, selectionStart);
            const tail = value.substring(selectionEnd);

            const newValue = head + shortcode + tail;
            yield put(change(formName, field, newValue));

            selectionEnd = selectionStart = selectionStart + shortcode.length; // Move cursor

            if (activeElement) {
                activeElement.focus();
                requestAnimationFrame(((start, end) => () => {
                    activeElement.setSelectionRange(start, end);
                })(selectionStart, selectionEnd));
            }
        },
    );

    // Watch focus changes
    try {
        while (true) {
            const { meta } = yield take(actionTypes.FOCUS);

            // Force autodetect of selection during insert
            selectionStart = null;
            selectionEnd = null;

            // Fields is not watched
            if (fields.indexOf(meta.field) === -1) {
                // Unknown field - close toolbar
                const toolbatState: ToolbarState = yield select(getToolbarState, formName);
                if (toolbatState && toolbatState.opened === ToolbarAction.EMOJIS) {
                    yield put(toolbarCloseAll(formName));
                }
                // Reset field to default
                field = defaultField;

                continue;
            }

            field = meta.field;

            if (document.activeElement instanceof HTMLInputElement || document.activeElement instanceof HTMLTextAreaElement) {
                activeElement = document.activeElement;
            }
        }
    } finally {
        // Cancel subtask
        yield cancel(task);
    }
}

function* watchEmojis(action: ToolbarWatchEmojiStartAction) {
    // Start watcher
    const task = yield fork(startEmojisWatch, action);

    // Wait for stop action
    yield take((stopAction: ToolbarWatchEmojiStopAction) => (
        stopAction.type === TOOLBAR_WATCH_EMOJI_STOP && stopAction.formName === action.formName
    ));

    // Cacncel watch
    yield cancel(task);
}

function* preparePhotosUpload({ formName, config, files, processPhotoUploadOnFormPage, accessKey, xhr }: ToolbarPreparePhotosUploadTriggerAction & XHRAction) {
    try {
        yield put(preparePhotosUploadApi.request(formName, config, files));
        yield put(photosUploadTrigger(formName, config, files, processPhotoUploadOnFormPage, accessKey));
        const photoPreview = [];
        for (let i = 0; i < files.length; i++) {
            photoPreview.push(yield call(() => getPhotoPreview(files[i])));
        }
        const normalizedResponse: TopicCoverUploadNormalizedResponse = normalize(
            {photoPreview},
            {photoPreview: [PhotoPreviewSchema]},
        );
        yield put(preparePhotosUploadApi.success(formName, config, files, normalizedResponse));
    } catch (error) {
        yield put(preparePhotosUploadApi.failure(formName, config, files, error));
    }
}

export default function* root() {
    yield all([
        takeLatest(TOOLBAR_PREPARE_PHOTOS_UPLOAD_TRIGGER, preparePhotosUpload),
        takeLatest(SOFT_KEYBOARD_TOGGLE, keyboardToggle),
        takeLatest(ORIENTATION_CHANGED, orientationChanged),
        takeEvery(TOOLBAR_WATCH_EMOJI_START, watchEmojis),
    ]);
}
